import React, { useState, useCallback } from 'react';
import { getDecisions, updateDecision } from '../../api/decisions';
import useApi from '../../hooks/useApi';
import TopBar from '../layout/TopBar';
import LoadingState from '../shared/LoadingState';
import ErrorState from '../shared/ErrorState';
import Modal from '../shared/Modal';
import { DECISION_STATUS_LABELS } from '../../utils/constants';
import { formatDate } from '../../utils/dates';

const STATUS_TABS = ['pendiente', 'tomada', 'descartada'];

function RegisterModal({ decision, onClose, onSave }) {
  const [chosenOption, setChosenOption] = useState('');
  const [rationale, setRationale] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!chosenOption.trim()) { setError('Debes indicar qué elegiste'); return; }
    setSaving(true);
    setError(null);
    try {
      const res = await updateDecision(decision.id, {
        chosen_option: chosenOption.trim(),
        rationale: rationale.trim() || null,
        status: 'tomada',
      });
      onSave(res.data);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal isOpen={true} onClose={onClose} title="Registrar decisión" size="md">
      <div style={{ marginBottom: 16, fontSize: 14, color: 'var(--text-muted)' }}>
        Decisión: {decision.question}
      </div>
      <form onSubmit={handleSubmit} className="form-stack">
        {error && <div className="chat-result chat-result--error">{error}</div>}
        <label className="form-label">¿Qué elegiste?
          <input
            className="filter-input"
            value={chosenOption}
            onChange={e => setChosenOption(e.target.value)}
            placeholder="Ej: Azure DevOps"
            required
          />
        </label>
        <label className="form-label">¿Por qué?
          <textarea
            className="filter-input"
            rows={3}
            value={rationale}
            onChange={e => setRationale(e.target.value)}
            placeholder="Justificación de la decisión…"
          />
        </label>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
          <button type="button" className="btn-secondary" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Registrando…' : 'Registrar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function DecisionCard({ decision, onRegister, onDiscard }) {
  const isPending = decision.status === 'pendiente';
  const isTaken = decision.status === 'tomada';
  const statusColor = isPending ? '#FBBF24' : isTaken ? '#4CAF82' : '#6B7A99';
  const options = decision.analysis_json?.options || [];

  return (
    <div className="decision-card card" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: statusColor, fontWeight: 600, fontSize: 13 }}>
            {isTaken ? '✓ TOMADA' : isPending ? '🎯 PENDIENTE' : '— DESCARTADA'}
          </span>
          {isTaken && decision.decided_at && (
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>· {formatDate(decision.decided_at)}</span>
          )}
        </div>
        {decision.context_name && (
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{decision.context_name}</span>
        )}
      </div>

      <h3 style={{ fontSize: 16, margin: '0 0 12px', lineHeight: 1.4 }}>{decision.question}</h3>

      {isPending && options.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Opciones analizadas por la IA:
          </div>
          {options.map((opt, i) => (
            <div key={i} style={{ marginBottom: 10, paddingLeft: 8, borderLeft: '2px solid var(--border)' }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{String.fromCharCode(65 + i)}) {opt.label}</div>
              {opt.pros?.map((p, j) => <div key={j} style={{ fontSize: 13, color: '#4CAF82' }}>✓ {p}</div>)}
              {opt.cons?.map((c, j) => <div key={j} style={{ fontSize: 13, color: '#FF6B6B' }}>✗ {c}</div>)}
            </div>
          ))}
        </div>
      )}

      {isPending && decision.recommendation && (
        <div style={{ background: 'rgba(108,142,255,0.1)', border: '1px solid rgba(108,142,255,0.2)', borderRadius: 6, padding: '10px 12px', marginBottom: 12, fontSize: 14 }}>
          💡 <strong>Recomendación:</strong> {decision.recommendation}
        </div>
      )}

      {isTaken && (
        <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>
          → Elegido: <strong>{decision.chosen_option}</strong>
          {decision.rationale && <div>→ Porque: {decision.rationale}</div>}
        </div>
      )}

      {isPending && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button className="btn-primary" onClick={() => onRegister(decision)}>Registrar decisión</button>
          <button className="btn-secondary" onClick={() => onDiscard(decision.id)}>Descartar</button>
        </div>
      )}
    </div>
  );
}

function DecisionsView({ showToast }) {
  const [statusTab, setStatusTab] = useState('pendiente');
  const [registerModal, setRegisterModal] = useState(null);

  const apiFn = useCallback(() => getDecisions({ status: statusTab, limit: 50 }), [statusTab]);
  const { data, loading, error, refetch } = useApi(apiFn, [statusTab]);

  async function handleDiscard(id) {
    try {
      await updateDecision(id, { status: 'descartada' });
      refetch();
      showToast?.('Decisión descartada', 'success');
    } catch (err) {
      showToast?.(err.message, 'error');
    }
  }

  function handleSave() {
    refetch();
    showToast?.('Decisión registrada', 'success');
  }

  const total = data?.length || 0;

  return (
    <div className="view-container">
      <TopBar title="Decisiones" subtitle={statusTab === 'pendiente' ? `${total} pendiente${total !== 1 ? 's' : ''}` : ''} />

      <div className="view-content">
        <div className="tabs-bar" style={{ marginBottom: 20 }}>
          {STATUS_TABS.map(tab => (
            <button
              key={tab}
              className={`tab-btn${statusTab === tab ? ' active' : ''}`}
              onClick={() => setStatusTab(tab)}
            >
              {DECISION_STATUS_LABELS[tab]}
              {tab === statusTab && data && ` (${total})`}
            </button>
          ))}
        </div>

        {loading && <LoadingState rows={4} />}
        {error && <ErrorState message={error} onRetry={refetch} />}
        {!loading && !error && (
          data?.length ? (
            data.map(d => (
              <DecisionCard
                key={d.id}
                decision={d}
                onRegister={setRegisterModal}
                onDiscard={handleDiscard}
              />
            ))
          ) : (
            <div className="empty-state">No hay decisiones {DECISION_STATUS_LABELS[statusTab]?.toLowerCase()}s.</div>
          )
        )}
      </div>

      {registerModal && (
        <RegisterModal
          decision={registerModal}
          onClose={() => setRegisterModal(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

export default DecisionsView;
